// src/pages/ManageSubscription.tsx
import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import fetchWithAuth from '../utils/fetchWithAuth'
import { useRevenueCat } from '../hooks/useRevenueCat'
import BackButton from '../components/ui/atoms/BackButton'
import { Button } from '../components/ui/atoms/Button/Button'
import BrandSpinner from '../components/ui/atoms/BrandSpinner/BrandSpinner'
import { BsStripe } from 'react-icons/bs'
import { LuExternalLink } from 'react-icons/lu'

type StripeSubscription = {
  plan: string
  status: 'active' | 'canceled' | 'trialing'
  trial_end?: number
  current_period_end?: number
  start_date?: number
  ended_on?: number
  portalUrl?: string | null
}

const formatDate = (unixSec?: number) =>
  unixSec
    ? new Date(unixSec * 1000).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '—'

const ManageSubscriptionPage: React.FC = () => {
  const navigate = useNavigate()
  const { backendUser } = useAuth() as any

  // Stripe/web state
  const [stripeSub, setStripeSub] = useState<StripeSubscription | null>(null)
  const [stripeLoading, setStripeLoading] = useState(true)
  const [stripeError, setStripeError] = useState<string | null>(null)

  // RevenueCat/native state
  const {
    initialized,
    customerInfo,
    restorePurchases,
    showManageSubscriptions,
  } = useRevenueCat()
  const [rcLoading, setRcLoading] = useState(true)

  // Fetch Stripe info
  const fetchStripeInfo = useCallback(async () => {
    if (!backendUser?.stripeCustomerId) {
      setStripeLoading(false)
      return
    }
    setStripeLoading(true)
    setStripeError(null)
    try {
      const data = await fetchWithAuth('/api/subscription/info')
      setStripeSub(data)
    } catch {
      setStripeError('Failed to load subscription details. Please try again.')
    } finally {
      setStripeLoading(false)
    }
  }, [backendUser])

  useEffect(() => {
    fetchStripeInfo()
  }, [fetchStripeInfo])

  // Restore RevenueCat purchases
  useEffect(() => {
    if (!backendUser?.appUserId || !initialized) {
      setRcLoading(false)
      return
    }
    restorePurchases().finally(() => setRcLoading(false))
  }, [backendUser, initialized, restorePurchases])

  // Show spinner while loading either platform
  if (stripeLoading || rcLoading) return <BrandSpinner />

  // No subscription IDs at all → redirect to paywall
  if (!backendUser?.stripeCustomerId && !backendUser?.appUserId) {
    navigate('/app/subscribe')
    return null
  }

  // ─── Web / Stripe branch ───
  if (backendUser.stripeCustomerId) {
    // Error fetching
    if (stripeError) {
      return (
        <div className="p-6">
          <BackButton onClick={() => navigate('/app/settings')} />
          <div className="mt-10 p-4 bg-red-50 text-red-700 rounded">
            {stripeError}
          </div>
          <Button 
            variant="black"
            className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
            onClick={fetchStripeInfo}>
            Retry
          </Button>
        </div>
      )
    }

    // Still loading
    if (!stripeSub) return <BrandSpinner />

    const {
      plan,
      status,
      trial_end,
      current_period_end,
      start_date,
      ended_on,
      portalUrl,
    } = stripeSub

    const startedOn  = formatDate(start_date)
    const nextDate   = status === 'trialing'
      ? formatDate(trial_end)
      : formatDate(current_period_end)
    // On "cancel at period end", Stripe leaves ended_on null until period lapses.
    // We treat the period end as the "ended on" date.
    const endDate    = ended_on
      ? formatDate(ended_on)
      : status === 'canceled'
      ? formatDate(current_period_end)
      : undefined

    // Active / trialing UI
    if (status === 'active' || status === 'trialing') {
      return (
        <div className="p-6 mt-10">
          <BackButton onClick={() => navigate('/app/settings')} />
          <h1 className="text-2xl font-semibold mb-4">Your Subscription</h1>

          <div className="bg-white p-4 space-y-2 mb-6">
            <p><strong>Plan:</strong> {plan}</p>
            <p><strong>Status:</strong> {status}</p>
            {status === 'trialing' ? (
              <p><strong>Trial ends:</strong> {nextDate}</p>
            ) : (
              <p><strong>Next billing:</strong> {nextDate}</p>
            )}
            {startedOn && <p><strong>Started on:</strong> {startedOn}</p>}
          </div>

          {portalUrl && (
            <div className="space-y-2 mb-4">
              <p className="text-sm text-gray-600 text-center">
                To update payment, view invoices, or cancel, use the Stripe portal.
              </p>
              <Button
                variant="black"
                className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
                onClick={() => window.open(portalUrl, '_blank')}
              >
                <BsStripe className="w-5 h-5" />
                View in Stripe Portal
                <LuExternalLink className="w-5 h-5" />
              </Button>
            </div>
          )}

          <Button
            variant="black"
            className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
            onClick={() => navigate('/app/subscribe')}
          >
            Re-subscribe
          </Button>
        </div>
      )
    }

    // Canceled UI
    return (
      <div className="p-6 mt-10">
        <BackButton onClick={() => navigate('/app/settings')} />
        <h1 className="text-2xl font-semibold mb-4">Subscription Ended</h1>

        <div className="bg-white p-4 space-y-2 mb-6">
          <p><strong>Plan:</strong> {plan}</p>
          {startedOn && <p><strong>Started on:</strong> {startedOn}</p>}
          <p><strong>Ended on:</strong> {endDate || '—'}</p>
        </div>

        <p className="text-sm text-gray-600 text-center mb-4">
          Your subscription has ended. To view your history, use the Stripe portal below.
        </p>

        {portalUrl && (
          <Button
            variant="black"
            className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark w-full flex items-center justify-center gap-2"
            onClick={() => window.open(portalUrl, '_blank')}
          >
            <BsStripe className="w-5 h-5" />
            View in Stripe Portal
            <LuExternalLink className="w-5 h-5" />
          </Button>
        )}

        <p className="text-sm text-gray-600 text-center my-4">
          To re-subscribe, tap “Re-subscribe”
        </p>

        <Button
          variant="black"
          className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
          onClick={() => navigate('/app/subscribe')}
        >
            Re-subscribe
        </Button>
      </div>
    )
  }

  // ─── Native / RevenueCat branch ───
  const activeEnt = Object.values(customerInfo?.entitlements.active || {})[0]
  if (!activeEnt) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <BackButton onClick={() => navigate('/app/settings')} />
        <div className="p-4 text-center">No active subscription found.</div>
      </div>
    )
  }

  const { identifier, isActive, willRenew, expirationDate } = activeEnt
  const statusText = isActive ? 'Active' : 'Inactive'
  const nextRcDate = expirationDate
    ? new Date(expirationDate).toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
    : '—'

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <BackButton onClick={() => navigate('/app/settings')} />
      <main className="flex-grow flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-gray-50 p-6">
          <h2 className="text-2xl font-semibold mb-2">{identifier}</h2>
          <p className="mb-1">Status: {statusText}</p>
          <p className="mb-4">
            {willRenew ? 'Renews on' : 'Expires on'} {nextRcDate}
          </p>
          <Button
            variant="black"
            className="bg-brandPurpleDark disabled:bg-brandPurpleLight disabled:text-brandPurpleDark"
            onClick={showManageSubscriptions}
          >
            Manage Subscriptions
          </Button>
        </div>
      </main>
    </div>
  )
}

export default ManageSubscriptionPage